import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Card, CardBody, CardHeader, Col, Container, Progress, Row } from 'reactstrap';
import Flatpickr from 'react-flatpickr';
import { toast } from 'react-toastify';
import { usePrivy } from '@privy-io/react-auth';

import {
  createMaintenanceWindowRest,
  deactivateMaintenanceWindowRest,
  getMaintenanceConfigRest,
  kickAllPlayersRest,
  MaintenanceConfigResponse,
  MaintenanceWindowRequest,
  setMaintenanceActiveRest,
  updateMaintenanceWindowRest,
} from '@/services/maintenance/maintenance-service';
import Button, { ButtonVariants } from '@/components/Button/Button';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import LoadingSpinner from '@/components/LoadingSpinner';
import { FeatureFlags, MaintenanceStatus } from '@repo/common-types';
import { format, formatDistanceToNow } from 'date-fns';
import { useFeatureFlagEnabled } from 'posthog-js/react';

const MaintenancePage: React.FC = () => {
  const { user } = usePrivy();
  const queryClient = useQueryClient();

  const isAuthorized = useFeatureFlagEnabled(FeatureFlags.ScheduledMaintenance);

  const [formData, setFormData] = useState<MaintenanceWindowRequest>({
    startTime: null,
    title: '',
    description: '',
    endTime: null,
  });
  const [showKickConfirm, setShowKickConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showSetActiveConfirm, setShowSetActiveConfirm] = useState(false);

  const isFormValid = useMemo(() => {
    return formData.title.length > 0 && formData.startTime;
  }, [formData]);

  // Query for maintenance config
  const {
    data: maintenanceConfig,
    isLoading,
    error,
    refetch,
  } = useQuery<MaintenanceConfigResponse>({
    queryKey: ['maintenanceConfig'],
    queryFn: getMaintenanceConfigRest,
    refetchInterval: 3000, // Refetch every 3 seconds for real-time updates
  });

  const timeRemaining = new Date(maintenanceConfig?.alert?.startTime ?? new Date());

  // Mutations
  const createMutation = useMutation({
    mutationFn: createMaintenanceWindowRest,
    onSuccess: () => {
      toast.success('Maintenance window scheduled successfully!');
      queryClient.invalidateQueries({ queryKey: ['maintenanceConfig'] });
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to schedule maintenance window');
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateMaintenanceWindowRest,
    onSuccess: () => {
      toast.success('Maintenance window updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['maintenanceConfig'] });
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update maintenance window');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: deactivateMaintenanceWindowRest,
    onSuccess: () => {
      toast.success('Maintenance window cancelled successfully!');
      queryClient.invalidateQueries({ queryKey: ['maintenanceConfig'] });
      setShowCancelConfirm(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to cancel maintenance window');
      setShowCancelConfirm(false);
    },
  });

  const kickMutation = useMutation({
    mutationFn: kickAllPlayersRest,
    onSuccess: () => {
      setShowKickConfirm(false);
      toast.success('Player kick process started!');
      queryClient.invalidateQueries({ queryKey: ['maintenanceConfig'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to start player kick process');
      setShowKickConfirm(false);
    },
  });

  const completeMutation = useMutation({
    mutationFn: deactivateMaintenanceWindowRest,
    onSuccess: () => {
      toast.success('Maintenance completed successfully!');
      queryClient.invalidateQueries({ queryKey: ['maintenanceConfig'] });
      setShowCompleteConfirm(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to complete maintenance');
      setShowCompleteConfirm(false);
    },
  });

  const setActiveMutation = useMutation({
    mutationFn: setMaintenanceActiveRest,
    onSuccess: () => {
      toast.success('Maintenance set to active!');
      queryClient.invalidateQueries({ queryKey: ['maintenanceConfig'] });
      setShowSetActiveConfirm(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to set maintenance as active');
      setShowSetActiveConfirm(false);
    },
  });

  // Status badge configuration
  const getStatusBadge = (status: MaintenanceStatus | null) => {
    const configs = {
      [MaintenanceStatus.SCHEDULED]: { color: 'info', text: 'Scheduled', icon: '🕒' },
      [MaintenanceStatus.WARNING]: { color: 'warning', text: 'Warning (2h)', icon: '⚠️' },
      [MaintenanceStatus.ACTIVE]: { color: 'danger', text: 'Active', icon: '🔴' },
      [MaintenanceStatus.KICKING]: { color: 'danger', text: 'Kicking Players', icon: '⚡' },
      [MaintenanceStatus.READY_FOR_UPGRADE]: { color: 'success', text: 'Ready for Upgrade', icon: '🚀' },
      [MaintenanceStatus.COMPLETED]: { color: 'success', text: 'Completed', icon: '✅' },
      [MaintenanceStatus.CANCELLED]: { color: 'secondary', text: 'Cancelled', icon: '❌' },
    };
    const config = configs[status as keyof typeof configs] || configs.scheduled;
    return (
      <Badge color={config.color} className="fs-6 px-3 py-2">
        <span className="me-2">{config.icon}</span>
        {config.text}
      </Badge>
    );
  };

  // Progress calculation
  const getKickProgress = useMemo(() => {
    if (!maintenanceConfig?.progress) return null;

    const { totalSeats, kickedSeats } = maintenanceConfig.progress;
    if (totalSeats === 0) return { percentage: 100, text: 'No seats to clear' };

    const percentage = Math.round((kickedSeats / totalSeats) * 100);
    return {
      percentage,
      text: `${kickedSeats} of ${totalSeats} seats cleared`,
    };
  }, [maintenanceConfig?.progress]);

  const resetForm = () => {
    setFormData({
      startTime: null,
      title: '',
      description: '',
      endTime: null,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.startTime) {
      toast.error('Please select a start time');
      return;
    }

    const data = {
      startTime: new Date(formData.startTime).toISOString(),
      endTime: formData.endTime ? new Date(formData.endTime).toISOString() : null,
      title: formData.title,
      description: formData.description || undefined,
    };

    if (maintenanceConfig?.alert) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  // Set form data when existing maintenance window is loaded
  useEffect(() => {
    if (maintenanceConfig?.alert && !formData.startTime) {
      setFormData({
        startTime: maintenanceConfig.alert.startTime,
        title: maintenanceConfig.alert.title || '',
        description: maintenanceConfig.alert.description || '',
        endTime: maintenanceConfig.alert.endTime,
      });
    }
  }, [maintenanceConfig?.alert]);

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const hasActiveWindow =
    maintenanceConfig?.alert && maintenanceConfig.status !== 'completed' && maintenanceConfig.status !== 'cancelled';
  const canKickPlayers = maintenanceConfig?.status === 'active' || maintenanceConfig?.status === 'scheduled';
  const canComplete =
    maintenanceConfig?.status === MaintenanceStatus.ACTIVE ||
    maintenanceConfig?.status === MaintenanceStatus.KICKING ||
    maintenanceConfig?.status === MaintenanceStatus.READY_FOR_UPGRADE;
  const isKicking = maintenanceConfig?.status === 'kicking';

  // Authorization check
  if (!isAuthorized) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">
            <h4 className="alert-heading">🚫 Access Denied</h4>
            <p>You don't have permission to access the maintenance control center.</p>
            <p className="mb-0">
              <small>
                Current user: {user?.email?.address || 'Unknown'}
                <br />
                Contact system administrator if you need access.
              </small>
            </p>
          </Alert>
        </Container>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <LoadingSpinner />
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">
            <h4 className="alert-heading">Error Loading Maintenance Data</h4>
            <p>Failed to load maintenance configuration. Please refresh the page.</p>
            <Button onClick={() => refetch()} variant={ButtonVariants.primary}>
              Retry
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-1">🔧 Maintenance Control Center</h4>
                <p className="text-muted mb-0">Manage scheduled maintenance windows and player operations</p>
              </div>
              {hasActiveWindow && <div className="text-end">{getStatusBadge(maintenanceConfig.status)}</div>}
            </div>
          </Col>
        </Row>

        <Row>
          {/* Current Status Card */}
          {hasActiveWindow && (
            <Col lg={6} className="mb-4">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light border-0">
                  <h5 className="mb-0">📊 Current Maintenance Status</h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-medium">Status:</span>
                      {getStatusBadge(maintenanceConfig.status)}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-medium">Scheduled Time:</span>
                      <span>{format(maintenanceConfig.alert!.startTime!, 'MM/dd/yyyy hh:mm a')}</span>
                    </div>

                    {timeRemaining && timeRemaining < new Date() && (
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="fw-medium">Time Remaining:</span>
                        <span className="badge bg-info">
                          {formatDistanceToNow(timeRemaining, { addSuffix: true, includeSeconds: true })}
                        </span>
                      </div>
                    )}

                    {maintenanceConfig.alert?.description && (
                      <div className="mb-3">
                        <span className="fw-medium">Custom Message:</span>
                        <div className="bg-light mt-1 rounded p-2">{maintenanceConfig.alert.description}</div>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar for Kicking */}
                  {isKicking && getKickProgress && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-medium">Player Kick Progress:</span>
                        <span className="small text-muted">{getKickProgress.text}</span>
                      </div>
                      <Progress
                        value={getKickProgress.percentage}
                        color="info"
                        className="mb-2"
                        style={{ height: '8px' }}
                      />
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="d-flex flex-wrap gap-2">
                    {canKickPlayers && (
                      <Button
                        variant={ButtonVariants.secondary}
                        onClick={() => setShowKickConfirm(true)}
                        disabled={kickMutation.isPending || isKicking}
                      >
                        {kickMutation.isPending ? <LoadingSpinner /> : '⚡ Kick All Players'}
                      </Button>
                    )}

                    {maintenanceConfig.status === MaintenanceStatus.READY_FOR_UPGRADE && (
                      <Button
                        variant={ButtonVariants.secondary}
                        onClick={() => setShowSetActiveConfirm(true)}
                        disabled={setActiveMutation.isPending}
                      >
                        {setActiveMutation.isPending ? <LoadingSpinner /> : '🔴 Set as Active'}
                      </Button>
                    )}

                    {canComplete && (
                      <Button
                        variant={ButtonVariants.primary}
                        onClick={() => setShowCompleteConfirm(true)}
                        disabled={completeMutation.isPending}
                      >
                        {completeMutation.isPending ? <LoadingSpinner /> : '✅ Complete Maintenance'}
                      </Button>
                    )}

                    {maintenanceConfig?.alert && (
                      <Button
                        variant={ButtonVariants.transparent}
                        onClick={() => setShowCancelConfirm(true)}
                        disabled={cancelMutation.isPending}
                      >
                        {cancelMutation.isPending ? <LoadingSpinner /> : '❌ Cancel'}
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}

          {/* Schedule/Edit Maintenance Form */}
          <Col lg={hasActiveWindow ? 6 : 8} className="mb-4">
            <Card className="border-0 shadow-sm">
              <CardHeader className="bg-light border-0">
                <h5 className="mb-0">
                  {hasActiveWindow ? '✏️ Update Maintenance Window' : '🕒 Schedule New Maintenance'}
                </h5>
              </CardHeader>
              <CardBody>
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="startTime" className="form-label fw-medium">
                      Maintenance Start Time <span className="text-danger">*</span>
                    </label>
                    <Flatpickr
                      className="form-control"
                      options={{
                        enableTime: true,
                        dateFormat: 'Y-m-d H:i',
                        time_24hr: true,
                        minDate: 'today',
                      }}
                      value={formData.startTime || undefined}
                      onChange={(dates) => setFormData((prev) => ({ ...prev, startTime: dates[0] || null }))}
                      placeholder="Select maintenance start time"
                    />
                    <div className="form-text">Players will receive a 2-hour warning before this time</div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="customMessage" className="form-label fw-medium">
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      id="title"
                      className="form-control"
                      value={formData.title}
                      onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter a title for the maintenance window"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="endTime" className="form-label fw-medium">
                      Maintenance End Time (Optional)
                    </label>
                    <Flatpickr
                      className="form-control"
                      options={{
                        enableTime: true,
                        dateFormat: 'Y-m-d H:i',
                        time_24hr: true,
                        minDate: formData.startTime || undefined,
                      }}
                      value={formData.endTime || undefined}
                      onChange={(dates) => setFormData((prev) => ({ ...prev, endTime: dates[0] || null }))}
                      placeholder="Select maintenance end time"
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="customMessage" className="form-label fw-medium">
                      Description (Optional)
                    </label>
                    <textarea
                      id="customMessage"
                      className="form-control"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter a custom message to display to players during maintenance..."
                      maxLength={500}
                    />
                    <div className="form-text">{500 - (formData.description?.length || 0)} characters remaining</div>
                  </div>

                  <div className="d-flex gap-2">
                    <Button disabled={!isFormValid} type="submit" variant={ButtonVariants.primary}>
                      {isFormLoading ? (
                        <LoadingSpinner />
                      ) : hasActiveWindow ? (
                        '💾 Update Maintenance'
                      ) : (
                        '🚀 Schedule Maintenance'
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant={ButtonVariants.transparent}
                      onClick={resetForm}
                      disabled={isFormLoading}
                    >
                      Clear Form
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </Col>

          {/* Info Card */}
          {!hasActiveWindow && (
            <Col lg={4} className="mb-4">
              <Card className="bg-light border-0 shadow-sm">
                <CardBody>
                  <h6 className="text-primary mb-3">💡 Maintenance Guide</h6>
                  <ul className="list-unstyled small">
                    <li className="mb-2">
                      <strong>🕒 Schedule:</strong> Set a future time for maintenance
                    </li>
                    <li className="mb-2">
                      <strong>⚠️ Warning:</strong> Players get 2-hour advance notice
                    </li>
                    <li className="mb-2">
                      <strong>🚫 Restrictions:</strong> Game actions are blocked automatically
                    </li>
                    <li className="mb-2">
                      <strong>⚡ Kick Players:</strong> Remove all players from cash tables
                    </li>
                    <li className="mb-0">
                      <strong>✅ Complete:</strong> End maintenance when done
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>

        {/* Error Display */}
        {maintenanceConfig?.progress?.errors && maintenanceConfig.progress.errors.length > 0 && (
          <Row>
            <Col>
              <Alert color="warning">
                <h6 className="alert-heading">⚠️ Kick Process Errors</h6>
                <ul className="mb-0">
                  {maintenanceConfig.progress.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Confirmation Modals */}
        {showKickConfirm && (
          <ConfirmModal
            onClose={() => setShowKickConfirm(false)}
            onConfirm={() => kickMutation.mutate()}
            title="⚡ Kick All Players"
            message="Are you sure you want to kick all players from cash tables? This action cannot be undone."
            confirmButtonText="Yes, Kick All Players"
            cancelButtonText="Cancel"
            disableConfirm={kickMutation.isPending}
          />
        )}

        {showCancelConfirm && (
          <ConfirmModal
            onClose={() => setShowCancelConfirm(false)}
            onConfirm={() => cancelMutation.mutate()}
            title="❌ Cancel Maintenance"
            message="Are you sure you want to cancel the scheduled maintenance window?"
            confirmButtonText="Yes, Cancel Maintenance"
            cancelButtonText="Keep Maintenance"
            disableConfirm={cancelMutation.isPending}
          />
        )}

        {showCompleteConfirm && (
          <ConfirmModal
            onClose={() => setShowCompleteConfirm(false)}
            onConfirm={() => completeMutation.mutate()}
            title="✅ Complete Maintenance"
            message="Are you sure you want to complete the maintenance? This will re-enable all game actions."
            confirmButtonText="Yes, Complete Maintenance"
            cancelButtonText="Continue Maintenance"
            disableConfirm={completeMutation.isPending}
          />
        )}

        {showSetActiveConfirm && (
          <ConfirmModal
            onClose={() => setShowSetActiveConfirm(false)}
            onConfirm={() => setActiveMutation.mutate()}
            title="🔴 Set Maintenance as Active"
            message="Are you sure you want to set maintenance as active? This will keep game actions restricted while you perform maintenance tasks."
            confirmButtonText="Yes, Set as Active"
            cancelButtonText="Cancel"
            disableConfirm={setActiveMutation.isPending}
          />
        )}
      </Container>
    </div>
  );
};

export default MaintenancePage;
